import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { isDefined } from '@fi-shared/utils';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { SelectSearchBase, TItemWithChildren } from '@fi-shared/fi-select-search/select-search-base';
import { FiDropdownItemDisabledFn } from '@fi-shared/fi-select-search/fi-select-search';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FiConnectedDropdownItemsComponent } from '@fi-shared/fi-connected-dropdown-items/fi-connected-dropdown-items.component';

@Component({
    selector: 'fi-menu-multi-select-item-search',
    templateUrl: './fi-menu-multi-select-item-search.component.html',
    styleUrls: ['./fi-menu-multi-select-item-search.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiMenuMultiSelectItemSearchComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiMenuMultiSelectItemSearchComponent,
            multi: true,
        },
    ],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-menu-multi-select-item-search' },
    standalone: false,
})
export class FiMenuMultiSelectItemSearchComponent<T extends { Id: number }, TRes = number>
    extends SelectSearchBase<T, number[]>
    implements OnInit
{
    @Input() items: T[] = [];
    @Output() itemSelectionChange = new EventEmitter<T>();
    @Input() displayValue: string;
    @Input() isProfileIconShown: boolean;
    @Input() profileIconName: string;
    @Input() isSearchByNameInputHidden: boolean;
    @Input() externalFilterFn: FiDropdownItemDisabledFn<T>;
    @Input() collapsedMode = false;
    FiProfileSizes = FiProfileSizes;

    @ViewChild(FiConnectedDropdownItemsComponent) connectedDropdownItemsComponent: FiConnectedDropdownItemsComponent<T>;

    public ngOnInit(): void {
        super.ngOnInit();
        //this.updateFilteredItems(); // todo looks like we don't need this

        if (!isDefined(this.items)) {
            this.items = [];
        }
        if (!isDefined(this.ngModel)) {
            this.ngModel = [];
        }

        if (this.isConnectedItemsLayout) {
            this.listItemBasePadding = 36;
            this.listItemPaddingPerLevel = 24;
            if (!this.collapsedMode) {
                this.setExpandableItems();
            }
            this.expandSelectedItems();
            setTimeout(() => this.cdr.markForCheck(), 200);
        }
    }

    protected getSelectedItemsFromModel(): T[] {
        return !isDefined(this.ngModel) ? [] : this.getFilteredItems();
    }

    protected updateFilteredItems() {
        const filteredItems = this.getFilteredItems();
        if (this.searchText && this.connectedDropdownItemsComponent) {
            this.connectedDropdownItemsComponent.expandItems(filteredItems as TItemWithChildren<T>[], {
                collapsePreviouslyExpandedItems: true,
            });
        }
        this.filteredItems$.next(filteredItems);
    }

    protected isItemNameIncludesSearchText(item: T): boolean {
        return !this.searchText || item[this.displayValue].toLowerCase().includes(this.searchText.toLowerCase());
    }

    private getFilteredItems(): T[] {
        if (this.isConnectedItemsLayout) {
            const filterFn = (item: TItemWithChildren<T>) =>
                this.isItemNameIncludesSearchText(item) && (!this.searchText || !this.isOptionDisabled(item));
            setTimeout(() => this.cdr.markForCheck());
            return this.deepChildrenSearch(this.itemGroups$.value, filterFn);
        }

        if (!isDefined(this.searchText) && !isDefined(this.externalFilterFn)) return this.items;

        return this.items.filter((item) => {
            if (this.searchText && !item[this.displayValue].toLowerCase().includes(this.searchText.toLowerCase())) {
                return false;
            }
            if (this.externalFilterFn) {
                return this.externalFilterFn(item, this.items);
            }
            return true;
        });
    }

    isItemSelected(item: T) {
        return this.ngModel.indexOf(item.Id) !== -1;
    }

    onItemClick($event: MouseEvent, item: T) {
        $event.stopPropagation();

        const itemIndex = this.ngModel?.indexOf(item.Id);
        if (itemIndex === -1) {
            this.ngModel.push(item.Id);
        } else {
            this.ngModel.splice(itemIndex, 1);
        }
        this.itemSelectionChange.emit(item);
        this.ngModelChange.emit(this.ngModel);
    }
}
